import axios from "axios";
import store from "@/store/index";
import env from "@/config/env";

const unauthorizedErrMsgs = ["No token or session id provided", "jwt expired", "Bearer token is invalid. Please provide a valid auth token"];
class APIService {
    axiosInstance = null;

    // readonly values
    MAX_RETRIES = 3;
    initialDelay = 1000;
    IGNORED_401_ENDPOINTS = ["auth/login", "auth/register", "auth/reset", "auth/initiate-reset", "/user/users/check-session"];
    PRIVATE_PAGES = ["/payments/verify", "/initiate-plan", "/callback"];
    
    constructor() {
        this.init();
        this.setupInterceptors();
    }

    init() {
        this.axiosInstance = axios.create({
            baseURL: `${env.VUE_APP_BASE_API_URL}/api`,
            headers: {
                "Content-Type": "application/json", 
            },
            withCredentials: true,
            timeout: 80000,
            timeoutErrorMessage: "Request timed out",
        });
    }

    setupInterceptors() {
        this.axiosInstance.interceptors.request.use(
            (config) => config,
            (error) => this.handleGeneralApiErrors(error),
        );

        this.axiosInstance.interceptors.response.use(
            (response) => this.handleGeneralApiResponse(response),
            (error) => this.handleGeneralApiErrors(error),
        );
    }

    async handleGeneralApiErrors(error) {
        const status = error?.response?.status || 500;
        const url = error?.config?.url || undefined;
        const retryCount = store.getters["auth/retryCount"] || 0;
        const isPublicEndpoint = this.IGNORED_401_ENDPOINTS.some(p => this.matchUrl(p, url));

        console.log({ apiErrors: error, isPublicEndpoint, url, status });

        // public endpoints: reject immediately
        if (isPublicEndpoint && url !== "/user/users/check-session") {
            throw error;
        } 

        // this is very likely a network error. don't send users back to the login page
        if (error?.isAxiosError) {
            if (status === 500 || !status) {
                console.log("status === 500 || !status");
                store.dispatch("auth/setNetworkErr", true);
                throw error;
            }
        }
    
        if (status === 401) {            
            const isTokenInvalidErr = unauthorizedErrMsgs.includes(error?.response?.data?.message);

            console.log("window.location.pathname", window.location.pathname);
            
            if (isTokenInvalidErr) {
                if (window.location.pathname.includes("dashboard") || this.PRIVATE_PAGES.includes(window.location.pathname)) {
                    console.log("!window.location.pathname.includes(\"auth\")");
                    return window.location.href = `${window.location.origin}/auth/logout`;
                }

                throw error;
            }  
        }

        if (status === 404 && url === "/user/users/fetchprofile") { // user account has been deleted or is unable to be fetched
            try {
                await store.dispatch("auth/signOut");
                return Promise.reject(error);
            } catch (signOutError) {
                console.error("Sign out failed:", signOutError);
                throw new Error("Authentication and logout failed");
            }
        }

        throw error;
    }

    calculateBackoff(retryCount) {
        const baseDelay = 1000; // 1 second
        return baseDelay * Math.pow(2, retryCount);
    }

    delay(ms) {        
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    matchUrl(pattern, url = "") {
        if (!url) return false;
        return url.includes(pattern);
    }

    handleGeneralApiResponse(response) {
        return response;
    }

    notifySupport(errorDetails = { error, endpoint }) {
        // Implement support notification logic
        console.error("Support notification:", errorDetails);
    }

    handleApiRequest() {
        // Implement retry request logic
        return Promise.resolve();
    }

    // Public methods for making requests
    async get(url = "", params = {}, config = {}) {
        return this.axiosInstance.get(url, params, config);
    }

    async post(url = "", data = {}, config = {}) {
        return this.axiosInstance.post(url, data, config);
    }

    async put(url = "", data = {}, config = {}) {
        return this.axiosInstance.put(url, data, config);
    }

    async patch(url = "", data = {}, config = {}) {
        return this.axiosInstance.patch(url, data, config);
    }

    async delete(url = "", config = {}) {
        return this.axiosInstance.delete(url, config);
    }

    // Public method to get axios instance if needed
    getInstance() {
        return this.axiosInstance;
    }
}

export default new APIService();